import { TIMER_HISTORY_KEY } from '../core/storage.js';
import { MAX_TIMER_MS, formatTimerTime } from './practice-timer.js';

export const TIMER_HISTORY_VERSION = 1;
export const TIMER_HISTORY_EXPORT_VERSION = 1;
export const TIMER_CUBE_ORDERS = Object.freeze([2, 3, 4, 5]);

function historyStorage(storage) {
  return storage ?? window.localStorage;
}

function normalizeRecord(record) {
  const order = Number(record?.order);
  const serial = Math.floor(Number(record?.serial));
  const recordedAt = new Date(record?.recordedAt);
  if (
    typeof record?.id !== 'string'
    || !record.id
    || !TIMER_CUBE_ORDERS.includes(order)
    || !Number.isInteger(serial)
    || serial < 1
    || Number.isNaN(recordedAt.getTime())
  ) return null;

  if (record.mode === 'dual') {
    const time1 = Math.floor(Number(record.time1));
    const time2 = Math.floor(Number(record.time2));
    if (
      !Number.isFinite(time1)
      || time1 < 0
      || time1 > MAX_TIMER_MS
      || !Number.isFinite(time2)
      || time2 < 0
      || time2 > MAX_TIMER_MS
    ) return null;

    const player1 = String(record.player1 ?? '').slice(0, 80);
    const player2 = String(record.player2 ?? '').slice(0, 80);
    const winnerPlayer = time1 === time2 ? 'tie' : time1 < time2 ? 'player1' : 'player2';
    const winner = winnerPlayer === 'tie'
      ? '平局'
      : winnerPlayer === 'player1' ? player1.trim() || '玩家1' : player2.trim() || '玩家2';

    return {
      id: record.id,
      serial,
      mode: 'dual',
      player1,
      player2,
      time1,
      time2,
      winner,
      winnerPlayer,
      order,
      recordedAt: recordedAt.toISOString(),
    };
  }

  const elapsedMs = Math.floor(Number(record?.elapsedMs));
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || elapsedMs > MAX_TIMER_MS) return null;

  return {
    id: record.id,
    serial,
    mode: 'single',
    elapsedMs,
    order,
    recordedAt: recordedAt.toISOString(),
    username: String(record.username ?? '').slice(0, 80),
  };
}

function sortNewestFirst(records) {
  return [...records].sort((left, right) => (
    new Date(right.recordedAt).getTime() - new Date(left.recordedAt).getTime()
    || right.serial - left.serial
  ));
}

function writeHistory(records, storage) {
  historyStorage(storage).setItem(TIMER_HISTORY_KEY, JSON.stringify({
    version: TIMER_HISTORY_VERSION,
    records: sortNewestFirst(records),
  }));
}

function createRecordId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `timer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function loadTimerHistory(storage) {
  const target = historyStorage(storage);
  const serialized = target.getItem(TIMER_HISTORY_KEY);
  if (!serialized) return [];
  try {
    const payload = JSON.parse(serialized);
    if (payload?.version !== TIMER_HISTORY_VERSION || !Array.isArray(payload.records)) throw new Error('invalid-history');
    return sortNewestFirst(payload.records.map(normalizeRecord).filter(Boolean));
  } catch {
    target.removeItem(TIMER_HISTORY_KEY);
    return [];
  }
}

/**
 * @param {{ elapsedMs: number, order: number, recordedAt?: Date | string, username?: string }} input
 * @param {{ storage?: any, id?: string }} [options]
 */
export function addTimerHistoryRecord(input, options = {}) {
  const { elapsedMs, order, recordedAt = new Date(), username = '' } = input;
  const { storage, id = createRecordId() } = options;
  const records = loadTimerHistory(storage);
  const record = normalizeRecord({
    id,
    serial: records.reduce((maximum, item) => Math.max(maximum, item.serial), 0) + 1,
    elapsedMs,
    order,
    recordedAt,
    username,
  });
  if (!record) throw new TypeError('历史记录数据无效');
  writeHistory([record, ...records], storage);
  return record;
}

/**
 * @param {{ player1?: string, player2?: string, time1: number, time2: number, order: number, recordedAt?: Date | string }} input
 * @param {{ storage?: any, id?: string }} [options]
 */
export function addDualTimerHistoryRecord(input, options = {}) {
  const {
    player1 = '',
    player2 = '',
    time1,
    time2,
    order,
    recordedAt = new Date(),
  } = input;
  const { storage, id = createRecordId() } = options;
  const records = loadTimerHistory(storage);
  const record = normalizeRecord({
    id,
    serial: records.reduce((maximum, item) => Math.max(maximum, item.serial), 0) + 1,
    mode: 'dual',
    player1,
    player2,
    time1,
    time2,
    order,
    recordedAt,
  });
  if (!record) throw new TypeError('双人历史记录数据无效');
  writeHistory([record, ...records], storage);
  return record;
}

export function getTimerHistoryRecord(id, storage) {
  return loadTimerHistory(storage).find((record) => record.id === id) ?? null;
}

export function updateTimerHistoryRecord(id, changes, storage) {
  const records = loadTimerHistory(storage);
  const index = records.findIndex((record) => record.id === id);
  if (index < 0) return null;
  const updated = normalizeRecord({ ...records[index], ...changes, id, serial: records[index].serial });
  if (!updated) throw new TypeError('历史记录数据无效');
  records[index] = updated;
  writeHistory(records, storage);
  return updated;
}

export function deleteTimerHistoryRecord(id, storage) {
  const records = loadTimerHistory(storage);
  const remaining = records.filter((record) => record.id !== id);
  if (remaining.length === records.length) return false;
  if (remaining.length) writeHistory(remaining, storage);
  else historyStorage(storage).removeItem(TIMER_HISTORY_KEY);
  return true;
}

export function clearTimerHistory(storage) {
  historyStorage(storage).removeItem(TIMER_HISTORY_KEY);
}

export function createTimerHistoryExport(storage, exportedAt = new Date()) {
  const date = new Date(exportedAt);
  if (Number.isNaN(date.getTime())) throw new TypeError('导出时间无效');
  return {
    version: TIMER_HISTORY_EXPORT_VERSION,
    exportedAt: date.toISOString(),
    records: loadTimerHistory(storage),
  };
}

function uniqueImportId(preferredId, usedIds, idFactory, serial) {
  if (typeof preferredId === 'string' && preferredId && !usedIds.has(preferredId)) return preferredId;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const generated = idFactory();
    if (typeof generated === 'string' && generated && !usedIds.has(generated)) return generated;
  }
  let suffix = 0;
  let fallback;
  do {
    fallback = `timer-import-${Date.now()}-${serial}-${suffix}`;
    suffix += 1;
  } while (usedIds.has(fallback));
  return fallback;
}

/**
 * @param {{ version: number, records: any[] }} payload
 * @param {{ storage?: any, idFactory?: () => string }} [options]
 */
export function importTimerHistory(payload, options = {}) {
  if (payload?.version !== TIMER_HISTORY_EXPORT_VERSION || !Array.isArray(payload.records)) {
    throw new TypeError('历史记录文件格式无效');
  }

  const { storage, idFactory = createRecordId } = options;
  const existing = loadTimerHistory(storage);
  const usedIds = new Set(existing.map((record) => record.id));
  let nextSerial = existing.reduce((maximum, record) => Math.max(maximum, record.serial), 0) + 1;
  const imported = [];

  payload.records.forEach((rawRecord) => {
    const id = uniqueImportId(rawRecord?.id, usedIds, idFactory, nextSerial);
    const normalized = normalizeRecord({ ...rawRecord, id, serial: nextSerial });
    if (!normalized) return;
    imported.push(normalized);
    usedIds.add(id);
    nextSerial += 1;
  });

  if (imported.length) writeHistory([...imported, ...existing], storage);
  return {
    importedCount: imported.length,
    skippedCount: payload.records.length - imported.length,
    records: imported,
  };
}

export function formatTimerHistoryExportFilename(value = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw new TypeError('导出时间无效');
  const pad = (part) => String(part).padStart(2, '0');
  return `cube-timer-history-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}.json`;
}

export function formatHistoryDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function toDateTimeLocalValue(value) {
  return formatHistoryDate(value).replace(' ', 'T');
}

export function parseTimerHistoryTime(value) {
  const match = /^(\d{2}):(\d{2})\.(\d{3})$/.exec(String(value).trim());
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  const milliseconds = Number(match[3]);
  if (minutes > 99 || seconds > (minutes === 99 ? 99 : 59)) return null;
  const elapsedMs = ((minutes * 60) + seconds) * 1000 + milliseconds;
  return elapsedMs <= MAX_TIMER_MS && formatTimerTime(elapsedMs) === value.trim() ? elapsedMs : null;
}
